import { useState, useEffect, useMemo } from 'react';
import { useGetSimulationDataByTenancyIdQuery } from '../../../store/services/report.service';

export default function useSimulationData(tenancyId) {
    const [dateRange, setDateRange] = useState('6months');

    const dateParams = useMemo(() => {
        const now = new Date();
        let startDate, endDate = now;

        switch (dateRange) {
            case '6months':
            default:
                startDate = new Date(now);
                startDate.setMonth(startDate.getMonth() - 6);
                break;
            case '12months':
                startDate = new Date(now);
                startDate.setMonth(startDate.getMonth() - 12);
                break;
        }

        return {
            startDate: startDate.toISOString().split('T')[0] + ' 00:00:00',
            endDate: endDate.toISOString().split('T')[0] + ' 23:59:59'
        };
    }, [dateRange]);

    const [simulationData, setSimulationData] = useState([]);

    const {
        data: apiSimulationData,
        isLoading: simulationDataLoading,
        error
    } = useGetSimulationDataByTenancyIdQuery({
        tenancyId,
        ...dateParams
    }, { skip: !tenancyId });

    useEffect(() => {
        if (apiSimulationData && !simulationDataLoading) {
            const processedData = processApiSimulationData(apiSimulationData, dateRange);
            setSimulationData(processedData);
        }
    }, [apiSimulationData, simulationDataLoading, dateRange]);

    return {
        simulationData,
        simulationDataLoading,
        dateRange,
        setDateRange
    };
}

function processApiSimulationData(apiData, dateRange) {
    // Calculate the cutoff date based on the selected range
    const cutoffDate = new Date();

    switch (dateRange) {
        case '6months':
        default:
            cutoffDate.setMonth(cutoffDate.getMonth() - 6);
            break;
        case '12months':
            cutoffDate.setMonth(cutoffDate.getMonth() - 12);
            break;
    }

    // Filter for simulations within the selected date range
    const recentSimulations = apiData.filter(item => {
        if (!item.DateSent) return false;
        const dateSent = new Date(item.DateSent);
        return dateSent >= cutoffDate;
    });

    // Group by SimulationId to count clicks and total sent
    const simulationCounts = recentSimulations.reduce((acc, item) => {
        if (!acc[item.SimulationId]) {
            acc[item.SimulationId] = {
                SimulationId: item.SimulationId,
                SimulationName: item.SimulationId,
                Clicks: 0,
                TotalSent: 0
            };
        }

        // Count as sent
        acc[item.SimulationId].TotalSent++;

        // Count as clicked if DateFailed is not null
        if (item.DateFailed) {
            acc[item.SimulationId].Clicks++;
        }

        return acc;
    }, {});

    // Convert to array and sort by percentage (Clicks / TotalSent, descending)
    // Add safety check for empty data
    if (Object.keys(simulationCounts).length === 0) {
        return [];
    }

    return Object.values(simulationCounts)
        .sort((a, b) => {
            const aPercent = a.TotalSent > 0 ? a.Clicks / a.TotalSent : 0;
            const bPercent = b.TotalSent > 0 ? b.Clicks / b.TotalSent : 0;
            return bPercent - aPercent;
        })
        .slice(0, 5); // Take top 5 simulations
}